import { sortOrderWithDefault } from '../../coaching-observation/experimental.grid';
import { createReducer } from '../../store.model';

import {
    ActionType,
    TagSetCacheAction,
    TagSetFilterMetricSelectionOptionsAction,
    TagSetFilterSelectionOptionsAction,
    TagSetInitAction,
    TagSetInitResultAction,
    TagSetSelectBehaviourAction,
    TagSetSelectRecentTagAction,
    TagSetSelectSubBehaviourAction,
    TagSetSortGridAction,
    TagSetToggleCategoryAction,
    TagSetToggleMetricAction,
} from './tags.action';
import {
    createCategories,
    createSelected,
    createSelection,
    createSelectionStr,
    dedup,
    filterMetricSelectionOptions,
    filterSelectionOptions,
    makeColumns,
    makeRows,
    makeRowsTop,
    mergeTags,
    sortTags,
    updateSelectionBehaviour,
    updateSelectionMetric,
    updateSelectionSubBehaviour,
} from './tags.function';
import { composeTagIdentifierNext } from './tags.model';
import { defaultSortDef, defaultState, TagSetState } from './tags.state';

const { builder, reducer } = createReducer<ActionType, TagSetState>(
    defaultState(),
);

builder
    .add<TagSetInitAction>(
        ActionType.COACHING_TAG_SET__INIT,
        (state, action) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    init: true,
                },
                performerId: action.payload.performerId,
                filterText:
                    state.performerId === action.payload.performerId
                        ? state.filterText
                        : '',
                filterMetricText:
                    state.performerId === action.payload.performerId
                        ? state.filterMetricText
                        : '',
            };
        },
    )
    .add<TagSetInitResultAction>(
        ActionType.COACHING_TAG_SET__INIT_RESULT,
        (state, action) => {
            const nextGridColumns = makeColumns();
            const coachableMetrics =
                action.payload.focusSummarySet.coachableMetrics;
            const activeMetrics = action.payload.activeCommitmentGoalMetrics;
            const nextMetrics = activeMetrics
                ? coachableMetrics.filter(
                      (e) => !activeMetrics.includes(e.metricId),
                  )
                : coachableMetrics;

            const rows = makeRows(nextMetrics);
            const { nextGridRowsTop, nextGridRows } = makeRowsTop(
                rows,
                action.payload.pinnedMetrics,
                action.payload.selected,
            );

            const { tags, teamTags } = mergeTags(
                state.cache,
                state.tags,
                sortTags(action.payload.focusSummarySet.user),
                sortTags(action.payload.focusSummarySet.team),
            );

            const nextSelection = createSelection(
                action.payload.behaviours,
                action.payload.subBehaviours,
                action.payload.focusSummarySet.coachableMetrics,
                action.payload.selected,
                nextGridRowsTop,
                nextGridRows,
            );

            const { tagItems, teamTagItems, selected } = createSelected(
                action.payload.behaviours,
                action.payload.subBehaviours,
                tags,
                teamTags,
                nextSelection,
            );

            const nextTagItemCategories = createCategories(
                tagItems,
                teamTagItems,
                state.tagItemCategories,
            );
            nextTagItemCategories.active.isCollapsed = false;
            nextTagItemCategories.closed.isCollapsed = false;
            nextTagItemCategories.team.isCollapsed = true;

            const { selectionBehaviourStr, selectionMetricsStr } =
                createSelectionStr(nextSelection);

            const { filteredBehaviours, filteredSubBehaviours } =
                filterSelectionOptions(
                    action.payload.focusSummarySet.userBehaviours,
                    action.payload.behaviours,
                    action.payload.subBehaviours,
                    nextSelection,
                    action.payload.activeMetricId,
                    state.filterText,
                );

            return {
                ...state,
                pending: {
                    ...state.pending,
                    init: false,
                },
                focusSummarySet: action.payload.focusSummarySet,
                behaviours: action.payload.behaviours,
                subBehaviours: action.payload.subBehaviours,
                activeMetricId: action.payload.activeMetricId,
                tags,
                teamTags,
                tagItemCategories: nextTagItemCategories,
                selected,
                selection: nextSelection,
                selectionBehaviourStr,
                selectionMetricsStr,
                filteredBehaviours,
                filteredSubBehaviours,
                coachableMetrics:
                    action.payload.focusSummarySet.coachableMetrics,
                gridColumns: nextGridColumns,
                gridRowsTop: nextGridRowsTop,
                gridRows: nextGridRows,
                activeUserCommitmentFocuses:
                    action.payload.focusSummarySet
                        .userActiveCommitmentGoalMetrics,
            };
        },
    );

builder.add<TagSetToggleCategoryAction>(
    ActionType.COACHING_TAG_SET__TOGGLE_CATEGORY,
    (state, action) => {
        const nextTagItemCategories = {
            ...state.tagItemCategories,
            [action.payload.category]: {
                ...state.tagItemCategories[action.payload.category],
                isCollapsed: action.payload.isCollapsed,
            },
        };

        return {
            ...state,
            tagItemCategories: nextTagItemCategories,
        };
    },
);

builder.add<TagSetSelectRecentTagAction>(
    ActionType.COACHING_TAG_SET__SELECT_RECENT_TAG,
    (state, action) => {
        const nextSelection = createSelection(
            state.behaviours,
            state.subBehaviours,
            state.coachableMetrics,
            {
                focusId: action.payload.tagItem.tag.focusId,
                userId: action.payload.tagItem.tag.userId,
                behaviourId: action.payload.tagItem.tag.behaviourId,
                subBehaviourId: action.payload.tagItem.tag.subBehaviourId,
                metrics: Array.from(state.selection.metrics.keys()),
            },
            state.gridRowsTop,
            state.gridRows,
        );

        const { tagItems, teamTagItems, selected } = createSelected(
            state.behaviours,
            state.subBehaviours,
            state.tags,
            state.teamTags,
            nextSelection,
        );

        const nextTagItemCategories = createCategories(
            tagItems,
            teamTagItems,
            state.tagItemCategories,
        );

        const { selectionBehaviourStr, selectionMetricsStr } =
            createSelectionStr(nextSelection);

        const { filteredBehaviours, filteredSubBehaviours } =
            filterSelectionOptions(
                state.focusSummarySet.userBehaviours,
                state.behaviours,
                state.subBehaviours,
                nextSelection,
                state.activeMetricId,
                state.filterText,
            );

        return {
            ...state,
            tagItemCategories: nextTagItemCategories,
            selected,
            selection: nextSelection,
            selectionBehaviourStr,
            selectionMetricsStr,
            filteredBehaviours,
            filteredSubBehaviours,
        };
    },
);

builder.add<TagSetFilterSelectionOptionsAction>(
    ActionType.COACHING_TAG_SET__FILTER_SELECTION_OPTIONS,
    (state, action) => {
        const { filteredBehaviours, filteredSubBehaviours } =
            filterSelectionOptions(
                state.focusSummarySet.userBehaviours,
                state.behaviours,
                state.subBehaviours,
                state.selection,
                state.activeMetricId,
                action.payload.text,
            );

        return {
            ...state,
            filterText: action.payload.text,
            filteredBehaviours,
            filteredSubBehaviours,
        };
    },
);

builder.add<TagSetFilterMetricSelectionOptionsAction>(
    ActionType.COACHING_TAG_SET__FILTER_METRIC_SELECTION_OPTIONS,
    (state, action) => {
        filterMetricSelectionOptions(
            state.gridRowsTop,
            state.gridRows,
            action.payload.text,
        );

        return {
            ...state,
            filterMetricText: action.payload.text,
        };
    },
);

builder.add<TagSetSelectBehaviourAction>(
    ActionType.COACHING_TAG_SET__SELECT_BEHAVIOUR,
    (state, action) => {
        const nextSelection = updateSelectionBehaviour(
            action.payload.behaviourId,
            state.selection,
            state.behaviours,
        );

        const { tagItems, teamTagItems, selected } = createSelected(
            state.behaviours,
            state.subBehaviours,
            state.tags,
            state.teamTags,
            nextSelection,
        );

        const nextTagItemCategories = createCategories(
            tagItems,
            teamTagItems,
            state.tagItemCategories,
        );

        const { selectionMetricsStr, selectionBehaviourStr } =
            createSelectionStr(nextSelection);

        const { filteredBehaviours, filteredSubBehaviours } =
            filterSelectionOptions(
                state.focusSummarySet.userBehaviours,
                state.behaviours,
                state.subBehaviours,
                nextSelection,
                state.activeMetricId,
                state.filterText,
            );

        return {
            ...state,
            tagItemCategories: nextTagItemCategories,
            selected,
            selection: nextSelection,
            selectionBehaviourStr,
            selectionMetricsStr,
            filteredBehaviours,
            filteredSubBehaviours,
        };
    },
);

builder.add<TagSetSelectSubBehaviourAction>(
    ActionType.COACHING_TAG_SET__SELECT_SUB_BEHAVIOUR,
    (state, action) => {
        const nextSelection = updateSelectionSubBehaviour(
            action.payload.subBehaviourId,
            state.selection,
            state.subBehaviours,
        );

        const { tagItems, teamTagItems, selected } = createSelected(
            state.behaviours,
            state.subBehaviours,
            state.tags,
            state.teamTags,
            nextSelection,
        );

        const nextTagItemCategories = createCategories(
            tagItems,
            teamTagItems,
            state.tagItemCategories,
        );

        const { selectionMetricsStr, selectionBehaviourStr } =
            createSelectionStr(nextSelection);

        return {
            ...state,
            tagItemCategories: nextTagItemCategories,
            selected,
            selection: nextSelection,
            selectionBehaviourStr,
            selectionMetricsStr,
        };
    },
);

builder.add<TagSetToggleMetricAction>(
    ActionType.COACHING_TAG_SET__TOGGLE_METRIC,
    (state, action) => {
        const nextSelection = updateSelectionMetric(
            action.payload.metricId,
            action.payload.checked,
            state.gridRowsTop,
            state.gridRows,
            state.selection,
            state.coachableMetrics,
        );

        const { tagItems, teamTagItems, selected } = createSelected(
            state.behaviours,
            state.subBehaviours,
            state.tags,
            state.teamTags,
            nextSelection,
        );

        const nextTagItemCategories = createCategories(
            tagItems,
            teamTagItems,
            state.tagItemCategories,
        );

        const { selectionMetricsStr, selectionBehaviourStr } =
            createSelectionStr(nextSelection);

        return {
            ...state,
            tagItemCategories: nextTagItemCategories,
            selected,
            selection: nextSelection,
            selectionBehaviourStr,
            selectionMetricsStr,
        };
    },
);

builder.add<TagSetSortGridAction>(
    ActionType.COACHING_TAG_SET__SORT_GRID,
    (state, action) => {
        const column = state.gridColumns.find(
            (e) => e.id === action.payload.columnId,
        );
        const nextGridSortDefs = sortOrderWithDefault(
            state.gridColumns,
            column,
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            state.gridSortDefs && state.gridSortDefs.length
                ? state.gridSortDefs[0]
                : null,
            defaultSortDef(),
        );

        return {
            ...state,
            gridSortDefs: nextGridSortDefs,
        };
    },
);

builder
    // TODO maybe try to put this in a root state rather than into parent's state so that it's global
    .add<TagSetCacheAction>(
        ActionType.COACHING_TAG_SET__CACHE,
        (state, action) => {
            const next =
                !action.payload.replace && state.cache ? [...state.cache] : [];
            if (action.payload.tags) {
                next.push(...action.payload.tags.map((e) => ({ ...e })));
            }
            const nextCache = dedup(next, (e) => composeTagIdentifierNext(e));

            return {
                ...state,
                cache: nextCache,
            };
        },
    );

export default {
    tagSet: reducer,
};
